import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Public share-import page. A therapist or caregiver shares their board via
 * the link from Profiles → "Share". The recipient hits this page and gets a
 * preview + one-tap "Import" button which copies the board into their own
 * account (custom photos and audio are intentionally NOT copied for privacy).
 */
export default function ShareImport() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [importError, setImportError] = useState("");
  const [busy, setBusy] = useState(false);
  const [overrideName, setOverrideName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/share/${token}/preview`);
        setPreview(data);
        setOverrideName(`${data.name} (copy)`);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "This link can't be loaded.");
      }
    })();
  }, [token]);

  const doImport = async () => {
    if (!user) {
      try { localStorage.setItem("aac_pending_import_token", token); } catch { /* ignore */ }
      nav(`/login?next=/import/${token}`);
      return;
    }
    setBusy(true);
    setImportError("");
    try {
      const { data } = await api.post(`/share/${token}/import`, { name: overrideName });
      try { localStorage.removeItem("aac_pending_import_token"); } catch { /* ignore */ }
      nav(`/board/${data.profile_id}`);
    } catch (e) {
      // Inline error below the confirm button — keeps preview visible
      setImportError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="import-error">
        <div className="brutal-card bg-white p-6 max-w-md text-center">
          <div className="display text-2xl font-bold mb-2">Can't open this board</div>
          <div className="text-[color:var(--ink-soft)] mb-4">{error}</div>
          <Link to="/" className="pill-btn">
            <ArrowLeft size={16} /> Home
          </Link>
        </div>
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="import-loading">
        <div className="display text-2xl">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8" data-testid="import-page">
      <div className="max-w-2xl mx-auto">
        <header className="flex items-center gap-3 mb-5">
          <Link to="/" className="pill-btn" data-testid="import-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold">Someone shared a board with you</h1>
        </header>

        <section className="brutal-card bg-[color:var(--gold)] p-6 mb-5 flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-4xl"
            style={{ background: preview.color }}
            aria-hidden
          >
            {preview.avatar}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)]">Shared board</div>
            <div className="display text-3xl font-extrabold leading-tight">{preview.name}</div>
            <div className="text-sm text-[color:var(--ink-soft)]">
              {preview.category_count} categories · {preview.tile_count} tiles · {preview.language.toUpperCase()}
            </div>
          </div>
        </section>

        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2 flex items-center gap-2">
            <Sparkles size={18} className="text-[color:var(--coral)]" /> What you'll get
          </h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            A copy of all categories and tiles will appear in your account as a new board you can rename, edit,
            and personalize. <strong>Custom photos and voice recordings are NOT copied</strong> — for privacy,
            the original family's child voice stays with the original family.
          </p>
          <ul className="space-y-1.5 mb-2">
            {preview.categories.slice(0, 12).map((c, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="text-lg" aria-hidden>{c.emoji}</span>
                <span className="font-semibold">{c.name}</span>
                <span className="text-[color:var(--ink-soft)]">— {c.tile_count} tiles</span>
              </li>
            ))}
            {preview.categories.length > 12 && (
              <li className="text-sm text-[color:var(--ink-soft)]">… and {preview.categories.length - 12} more</li>
            )}
          </ul>
        </section>

        <section className="brutal-card bg-white p-5 mb-4">
          <label className="block">
            <span className="display font-semibold mb-1 block">Name your copy</span>
            <input
              className="brutal-input"
              value={overrideName}
              onChange={(e) => setOverrideName(e.target.value)}
              maxLength={60}
              data-testid="import-name"
            />
          </label>
        </section>

        <button
          type="button"
          className="pill-btn primary w-full"
          onClick={doImport}
          disabled={busy || !overrideName.trim()}
          data-testid="import-confirm"
        >
          <Download size={16} />
          {busy ? "Importing…" : user ? "Import to my account" : "Sign in &amp; import"}
        </button>

        {importError && (
          <div
            className="brutal-card bg-white p-3 mt-3 border-[color:var(--coral)] text-[color:var(--coral-deep)] display font-semibold"
            data-testid="import-inline-error"
          >
            {importError}
          </div>
        )}

        <p className="text-center text-xs text-[color:var(--ink-soft)] mt-4">
          By importing, you'll get your own copy you can edit. The original board isn't affected.
        </p>
      </div>
    </div>
  );
}
